import {CommonModule} from '@angular/common';
import {Component, Inject, Input} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {dateToString, EmTranslatePipe, stringToDate, validators} from '@emma-labs/shared/angular';
import {
  Beneficiary,
  BENEFICIARY_LINK_OPTIONS,
  BENEFICIARY_SELECT_BUTTONS,
  BeneficiaryLink,
  ENVIRONMENT,
  EnvironmentBroker,
  isProvinceRequiresTrusteeForBeneficiaryUnder19YearsOld,
  isTrusteeNeeded,
  Province,
  removeNullProperties,
  REVOCABLE_OPTIONS,
  Sex,
  SEX_OPTIONS,
  TRUSTEE_LINK_OPTIONS,
  TrusteeLink,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NgxMaskDirective} from 'ngx-mask';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DividerModule} from 'primeng/divider';
import {DropdownModule} from 'primeng/dropdown';
import {InputNumberModule} from 'primeng/inputnumber';
import {InputTextModule} from 'primeng/inputtext';
import {SelectButtonModule} from 'primeng/selectbutton';
import {debounceTime} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {DEBOUNCE_TIME, SHORT_DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {BaseSectionComponent} from '../../base-section/base-section.component';

interface BeneficiariesSectionForm {
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  birthday: FormControl<string | null>;
  link: FormControl<BeneficiaryLink | null>;
  revocable: FormControl<boolean | null>;
  share: FormControl<number | null>;
  trustee?: FormGroup<TrusteeSectionForm>;
}

interface TrusteeSectionForm {
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  birthday: FormControl<string | null>;
  sex: FormControl<Sex | null>;
  link: FormControl<TrusteeLink | null>;
}

type TrusteeSectionFormRaw = {
  [K in keyof TrusteeSectionForm]: TrusteeSectionForm[K] extends FormControl<infer T> ? T | null : never;
};

@UntilDestroy()
@Component({
  selector: 'em-beneficiaries-section',
  templateUrl: './beneficiaries-section.component.html',
  styleUrls: ['./beneficiaries-section.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    SelectButtonModule,
    DividerModule,
    ButtonModule,
    NgxMaskDirective,
    DropdownModule,
    InputNumberModule,
    InputTextModule,
    EmTranslatePipe,
  ],
})
export class BeneficiariesSectionComponent extends BaseSectionComponent<
  FormArray<FormGroup<BeneficiariesSectionForm>>,
  Beneficiary[]
> {
  readonly beneficiaryLinkOptions = BENEFICIARY_LINK_OPTIONS;
  readonly trusteeLinkOptions = TRUSTEE_LINK_OPTIONS;
  readonly revocableOptions = REVOCABLE_OPTIONS;
  readonly sexOptions = SEX_OPTIONS;
  readonly beneficiariesMethodSelectButtons = BENEFICIARY_SELECT_BUTTONS;

  totalShareLeft = 100;
  legalHeirFormControl: FormControl<boolean | null>;
  _province!: Province;

  @Input({required: true}) totalCoverage!: number;

  @Input({required: true}) set province(province: Province) {
    this._province = province;
    this.form.controls.forEach((beneficiaryForm) => this.addOrRemoveTrustee(beneficiaryForm));
    this.form.markAllAsTouched();
  }

  @Input()
  override set readonly(isReadonly: boolean) {
    if (isReadonly) {
      super.readonly = isReadonly;
      this.legalHeirFormControl.disable({emitEvent: false});
    }
  }

  @Input({required: true})
  override set props(beneficiaries: Beneficiary[] | undefined) {
    if (!this.isPropsReceived && beneficiaries) {
      const isArrayEmpty = beneficiaries.length === 0;
      this.legalHeirFormControl.patchValue(isArrayEmpty, {emitEvent: false});
      if (isArrayEmpty) {
        this.isPropsReceived = true;
        return;
      }
      beneficiaries.forEach((b) =>
        this.form.push(
          this.createBeneficiaryFormGroup(b.trustee !== undefined || this.needsTrustee(b.birthday)),
          {emitEvent: false},
        ),
      );
      super.props = beneficiaries;
      this.totalShareLeft = this.getTotalShareLeft();
    }
  }

  constructor(
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();
    this.legalHeirFormControl = fb.control(null);
    this.legalHeirFormControl.valueChanges.pipe(untilDestroyed(this)).subscribe((isLegalHeir) => {
      if (isLegalHeir) {
        this.resetForm();
        this.propsChange.emit({output: []});
      } else {
        this.form.push(this.createBeneficiaryFormGroup(), {emitEvent: false});
      }
    });
  }

  addEmptyBeneficiary(): void {
    this.form.push(this.createBeneficiaryFormGroup(), {emitEvent: false});
  }

  removeBeneficiary(index: number): void {
    this.form.removeAt(index);
    this.totalShareLeft = this.getTotalShareLeft();
  }

  coverageShare(i: number): number {
    return ((this.form.at(i).controls.share.value ?? 0) / 100) * this.totalCoverage;
  }

  hasTrustee(): boolean {
    return this.form.controls.some((b) => b.controls.trustee);
  }

  simulateBeneficiaries(): void {
    this.form.controls = [this.createBeneficiaryFormGroup(), this.createBeneficiaryFormGroup(true)];
    this.form.setValue([
      {
        firstName: 'Samuel',
        lastName: 'Ouellet',
        share: 43,
        link: BeneficiaryLink.FATHER,
        revocable: true,
        birthday: dateToString(new Date(new Date().getFullYear() - 22, new Date().getMonth())),
      },
      {
        firstName: 'Jean-Michel',
        lastName: 'Bellavance',
        share: 57,
        link: BeneficiaryLink.BROTHER,
        revocable: false,
        birthday: dateToString(new Date(new Date().getFullYear() - 15, new Date().getMonth())),
        trustee: {
          firstName: 'Roger',
          lastName: 'Bellavance',
          sex: Sex.MALE,
          link: TrusteeLink.FATHER,
          birthday: dateToString(new Date(new Date().getFullYear() - 48, new Date().getMonth())),
        },
      },
    ]);
  }

  override formToOutput(): Beneficiary[] {
    const values = this.form.getRawValue().map((v) => ({...removeNullProperties(v)}));
    return values.map(
      (v) =>
        ({
          ...v,
          birthday: v.birthday ? stringToDate(v.birthday) : undefined,
          trustee: v.trustee
            ? {
                ...v.trustee,
                birthday: v.trustee.birthday ? stringToDate(v.trustee.birthday) : undefined,
              }
            : undefined,
        }) as unknown as Beneficiary,
    );
  }

  private createForm(): FormArray<FormGroup<BeneficiariesSectionForm>> {
    const form = this.fb.array<FormGroup<BeneficiariesSectionForm>>([], [validators.totalShareHundred]);

    form.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe(() => {
        this.propsChange.emit({output: this.formToOutput()});
      });

    return form;
  }

  private createBeneficiaryFormGroup(withTrustee = false): FormGroup<BeneficiariesSectionForm> {
    const beneficiaryForm = this.fb.group<BeneficiariesSectionForm>({
      firstName: this.fb.control(null, [
        Validators.required,
        Validators.maxLength(25),
        validators.noSpecialCharacter,
        validators.noNumber,
      ]),
      lastName: this.fb.control(null, [
        Validators.required,
        Validators.maxLength(25),
        validators.noSpecialCharacter,
        validators.noNumber,
      ]),
      link: this.fb.control(null, [Validators.required]),
      birthday: this.fb.control(null, [Validators.required, validators.date, validators.dateInFuture]),
      revocable: this.fb.control(null, [Validators.required]),
      share: this.fb.control(null, [Validators.required, validators.percentage, Validators.min(1)]),
    });

    if (withTrustee) {
      beneficiaryForm.setControl('trustee', this.createTrusteeFormGroup());
    }

    beneficiaryForm.controls.share.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(SHORT_DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe(() => {
        this.totalShareLeft = this.getTotalShareLeft();
      });

    beneficiaryForm.controls.birthday.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe(() => {
        this.addOrRemoveTrustee(beneficiaryForm);
      });

    return beneficiaryForm;
  }

  private addOrRemoveTrustee(beneficiaryForm: FormGroup<BeneficiariesSectionForm>): void {
    const birthday = beneficiaryForm.controls.birthday.value;
    if (birthday != null && this.needsTrustee(stringToDate(birthday))) {
      if (!beneficiaryForm.controls.trustee) {
        beneficiaryForm.addControl('trustee', this.createTrusteeFormGroup(), {emitEvent: false});
      } else {
        beneficiaryForm.setControl(
          'trustee',
          this.createTrusteeFormGroup(beneficiaryForm.controls.trustee!.getRawValue()),
          {emitEvent: false},
        );
      }
    } else {
      beneficiaryForm.removeControl('trustee', {emitEvent: false});
    }
  }

  private createTrusteeFormGroup(trustee?: TrusteeSectionFormRaw): FormGroup<TrusteeSectionForm> {
    return this.fb.group<TrusteeSectionForm>({
      firstName: this.fb.control(trustee?.firstName ?? null, [
        Validators.required,
        Validators.maxLength(25),
        validators.noSpecialCharacter,
        validators.noNumber,
      ]),
      lastName: this.fb.control(trustee?.lastName ?? null, [
        Validators.required,
        Validators.maxLength(25),
        validators.noSpecialCharacter,
        validators.noNumber,
      ]),
      sex: this.fb.control(trustee?.sex ?? null, [Validators.required]),
      link: this.fb.control(trustee?.link ?? null, [Validators.required]),
      birthday: this.fb.control(trustee?.birthday ?? null, [
        Validators.required,
        isProvinceRequiresTrusteeForBeneficiaryUnder19YearsOld(this._province)
          ? validators.atLeast19YearsOld
          : validators.atLeast18YearsOld,
        validators.date,
        validators.dateInFuture,
      ]),
    });
  }

  private getTotalShareLeft(): number {
    return (
      100 - this.form.controls.map((b) => b.controls.share.value ?? 0).reduce((curr, acc) => curr + acc, 0)
    );
  }

  private resetForm(): void {
    this.form.controls = [];
    this.form.reset([], {emitEvent: false});
  }

  private needsTrustee(birthday?: Date): boolean {
    if (!birthday) {
      return false;
    }
    return isTrusteeNeeded(birthday, this._province);
  }
}
